import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { IQueryParam, IFilter, ILogEvento } from 'modelos/src';
import { Subscription } from 'rxjs';
import { IColumnas } from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { LoginService } from '../../../login/login.service';
import { MatDialog } from '@angular/material/dialog';
import { fechaLogHtml } from '../fecha-log';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import { LogEventoLoraService } from '../../../../auxiliares/servicios/http/log-evento-lora.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { PayloadParserService } from '../../../../auxiliares/payload-parser-dialog/payload-parser.service';

@Component({
  selector: 'app-listado-eventos-lora',
  templateUrl: './listado-eventos-lora.component.html',
  styleUrl: './listado-eventos-lora.component.scss',
  standalone: false,
})
export class ListadoEventosLoraComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  matDialog = inject(MatDialog);
  logEventoMqttService = inject(LogEventoLoraService);
  private payloadParserService = inject(PayloadParserService);

  public clientePropio = LoginService.getCliente();
  //// Tabla nueva
  public name = 'ListadoEventosLoraComponent';
  public logEvento?: ILogEvento[];
  public totalCount = 0;
  public columnas?: IColumnas<ILogEvento>[];

  //Subs
  public logEvento$?: Subscription;

  public loading = false;

  public logEventoSeleccionado?: ILogEvento;

  //Buscar
  public search: IRegExpSearch = {
    fields: ['deveui', 'deviceName'],
  };

  // FILTROS
  //Cliente
  //Fecha de creación
  private fechaCreacion: IFiltroTabla = {
    filter: {
      field: 'fechaCreacion',
    },
    label: 'Buscar por fecha',
    tipo: 'dateRange',
  };

  private deveui: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'deveui',
    },
    label: 'DevEUI',
    selectLabel: 'deveui',
    selectValue: 'deveui',
    tipo: 'ngselect-async',
    asyncFunction: 'logEventos',
    searchOn: ['deveui'],
  };

  private puerto: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'puerto',
    },
    label: 'Puerto',
    tipo: 'input',
  };

  //Tipo de evento
  private tipo: IFiltroTabla = {
    elementos: ['up', 'down', 'status', 'join', 'ack', 'txack'],
    filter: {
      field: 'tipo',
    },
    label: 'Buscar por tipo',
    tipo: 'ngselect',
  };

  public filtros: IFiltroTabla[] = [
    this.fechaCreacion,
    this.tipo,
    this.deveui,
    this.puerto,
  ];

  //Query
  public filter: IFilter<ILogEvento> = {};

  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
    select: '-__v',
    excludeTotalCount: true,
  };

  // Listars
  public async listar(): Promise<void> {
    this.logEvento$?.unsubscribe();
    this.logEvento$ = this.lista2
      .logEventos(this.query, 30)
      .subscribe((data) => {
        this.totalCount = HelperService.generarTotalCount(
          this.query,
          data.datos.length,
        );
        this.logEvento = data.datos;
      });
  }

  // Acciones

  public verDetalles(dato: ILogEvento): void {
    this.logEventoSeleccionado = dato;
  }

  public cerrarDetalles(): void {
    this.logEventoSeleccionado = undefined;
  }

  public parsearPayload(dato: ILogEvento): void {
    if (dato.payload && typeof dato.payload === 'string') {
      this.payloadParserService.openPayloadParser(dato.payload);
    } else {
      console.warn('No hay payload válido para parsear:', dato);
    }
  }
  /////////////////////////////////////////////////////////////
  // Tabla

  private setColumnas() {
    const columnas: IColumnas<ILogEvento>[] = [
      // Fecha (con segundos: en los logs importan)
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          html: (dato) => fechaLogHtml(dato.fechaCreacion),
          noData: 'Sin fecha',
        },
      },

      // Tipo
      {
        header: {
          label: 'Tipo',
        },
        row: {
          field: 'tipo',
          noData: 'Sin tipo',
        },
      },

      // DevEUI
      {
        header: {
          label: 'DevEUI',
        },
        row: {
          html: (dato) =>
            dato.deveui
              ? `<span class="tabla-codigo">${escaparHtml(dato.deveui)}</span>`
              : '',
          noData: 'Sin DevEUI',
        },
      },

      // Dispositivo
      {
        header: {
          label: 'Dispositivo',
        },
        row: {
          field: 'deviceName',
          noData: 'Sin nombre',
        },
      },

      // Puerto
      {
        header: {
          label: 'Puerto',
        },
        row: {
          field: 'puerto',
          noData: 'Sin puerto',
        },
      },

      // FCnt
      {
        header: {
          label: 'FCnt',
        },
        row: {
          field: 'fCnt',
          noData: 'Sin FCnt',
        },
      },
      // ToA (Time on Air)
      {
        header: {
          label: 'ToA',
          sortable: false,
        },
        row: {
          parse(dato) {
            const toa = dato.metadatosUplink?.timeOnAir;
            return toa !== undefined && toa !== null
              ? `${toa.toLocaleString('es-AR')} ms`
              : undefined;
          },
          noData: 'Sin ToA',
        },
      },
      // Payload (up/down) o margin (status)
      {
        header: {
          label: 'Payload',
        },
        row: {
          html: (dato) => {
            if (dato.tipo === 'up' || dato.tipo === 'down') {
              return dato.payload
                ? `<span class="tabla-codigo">${escaparHtml(String(dato.payload))}</span>`
                : '';
            }
            if (dato.tipo === 'status') {
              return dato.margin !== undefined && dato.margin !== null
                ? `Margin: ${escaparHtml(String(dato.margin))}`
                : '';
            }
            return '';
          },
          noData: 'Sin payload',
        },
      },
      // Batería
      {
        header: {
          label: 'Batería',
        },
        row: {
          field: 'battery',
          noData: 'Sin batería',
        },
      },
      // Acciones sueltas: Parsear payload sólo aplica a up/down, y en un
      // menú ⋮ el resto de las filas mostrarían un menú vacío
      {
        header: {
          label: 'Acciones',
        },
        row: {
          field: 'acciones',
          acciones: [
            {
              icon: 'visibility',
              tooltip: 'Ver detalle',
              click: (dato) => this.verDetalles(dato),
            },
            {
              // Mismo ícono que el encabezado del diálogo que abre
              icon: 'data_object',
              tooltip: 'Parsear payload',
              click: (dato) => this.parsearPayload(dato),
              oculta: (dato) => dato.tipo !== 'up' && dato.tipo !== 'down',
            },
          ],
        },
      },
    ];

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  async ngOnDestroy() {
    this.logEvento$?.unsubscribe();
  }
}
